// Screen-level layout helpers for the back-office screens (DESIGN.md §6.3, §10). They only
// arrange packages/ui components; anything that is a component in its own right belongs there.
import { Kbd } from '@wodi/ui';
import type { ReactNode } from 'react';

import styles from './screen.module.css';

/** Lets a wide table scroll inside its own box, so the page never scrolls sideways. */
export function TableScroll({ children }: { children: ReactNode }) {
  return <div className={styles.tableScroll}>{children}</div>;
}

/**
 * A visible keyboard hint beside the action it names: "Press P to take payment." Takes the
 * same combo strings as `useShortcut` ("p", "mod+enter"); two combos read as a range
 * ("Press 1–4 to …"). The sentence is ordinary text, so screen readers read it too.
 */
export function ShortcutHint({
  combo,
  to,
  action,
}: {
  combo: string;
  /** The last key of a range, e.g. combo "1", to "4". */
  to?: string;
  action: string;
}) {
  return (
    <span className={styles.hint}>
      {'Press '}
      <Kbd combo={combo} />
      {to === undefined ? null : (
        <>
          {'–'}
          <Kbd combo={to} />
        </>
      )}
      {` to ${action}.`}
    </span>
  );
}

/**
 * Wraps a single-key shortcut handler so it does nothing while a dialog is open: a key
 * pressed on a dialog's checkbox must not act on the screen behind it.
 */
export function outsideDialogs(handler: () => void): () => void {
  return () => {
    if (document.querySelector('[role="dialog"], [role="alertdialog"]') !== null) return;
    handler();
  };
}

export { styles as screen };
